import { Injectable, inject } from '@angular/core'
import { ActivatedRoute } from '@angular/router'

import { BehaviorSubject, Observable, of, switchMap, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'

import { environment } from '@flowx/designer/environment'
import {
  MEDIA_LIBRARY_API,
  MediaAppInfo,
  MediaLibraryPayloadData,
  MediaLibraryResource,
} from '@flowx/designer/modules/core/dictionary/media-library.dictionary'
import { PaginationQueryParams } from '@flowx/designer/shared/api/api-http.service'
import { MediaLibraryNominalApi } from '@flowx/designer/shared/api/media-library-nominal.api'
import { MediaLibraryApi } from '@flowx/designer/shared/api/media-library.api'
import { AppResource, ProxyApiAction } from '@flowx/designer/shared/entities/application.entity'
import { mapResource } from '@flowx/designer/shared/entities/application.entity'
import { MediaLibraryToastMessages } from '@flowx/designer/shared/entities/media-library.entity'
import {
  PaginatedEndpoint,
  PaginatedResponse,
  Sort,
} from '@flowx/designer/shared/services/paginated-datasource'
import { ToastMessageService } from '@flowx/designer/shared/services/toast-message.service'
import { jsonToBlob, parseFileAsJson } from '@flowx/shared/utils'

import { GridSearchService } from './grid-search.service'

const SORT_FIELD = 'modifiedDate'

@Injectable()
export class MediaLibraryService extends GridSearchService<MediaLibraryResource> {
  #route = inject(ActivatedRoute)
  #mediaLibraryApi = inject(MEDIA_LIBRARY_API)
  #toastMessageService = inject(ToastMessageService)
  private _importInProgress = new BehaviorSubject<boolean>(false)

  getGridEndpointInfo(): {
    endpoint: PaginatedEndpoint<any, MediaLibraryResource>
    sort: Sort
    query: Partial<any>
  } {
    return {
      endpoint: (request, query) =>
        this.getImages({ ...request, ...query, excludeSystemResources: true }),
      sort: {
        property: SORT_FIELD,
        direction: 'desc',
      },
      query: { keyword: this.#route.snapshot.queryParams?.searchQuery },
    }
  }

  getImages(
    params: PaginationQueryParams,
    includeGlobalAssets = false
  ): Observable<PaginatedResponse<MediaLibraryResource>> {
    return (this.#mediaLibraryApi as any)
      .getMediaLibraryResources({ ...params }, includeGlobalAssets)
      .pipe(
        map((response: any) => {
          const resources = response.resourceContent || response
          return {
            ...resources,
            content: resources.content.map((image) => this.prepareImageForUi(image)),
          }
        }),
        catchError((err) => {
          this.#toastMessageService.showErrorMessage()
          return throwError(() => err)
        })
      )
  }

  getAllResources(
    params: any
  ): Observable<{ list: MediaLibraryResource[]; appsInfo?: MediaAppInfo }> {
    return (this.#mediaLibraryApi as any)
      .getMediaLibraryResources({ sort: 'modifiedDate,desc', ...params }, true)
      .pipe(
        map(
          ({
            resourceContent,
            appsInfo,
          }: {
            resourceContent: MediaLibraryResource[]
            appsInfo?: MediaAppInfo
          }) => ({
            list: resourceContent.map((image) => this.prepareImageForUi(image)),
            appsInfo,
          })
        ),
        catchError((err) => {
          this.#toastMessageService.showErrorMessage()
          return throwError(() => err)
        })
      )
  }

  addFile(
    payloadData: MediaLibraryPayloadData,
    options?: { suppressToasts?: boolean }
  ): Observable<any> {
    const formData = this.getFormData(payloadData, ProxyApiAction.CREATE)

    return this.#mediaLibraryApi.addFile(formData).pipe(
      tap(() => {
        this.refresh()
        if (!options?.suppressToasts) {
          this.#toastMessageService.showSuccessMessage(MediaLibraryToastMessages.CREATE_SUCCESS)
        }
      }),
      catchError((err) => {
        if (!options?.suppressToasts) {
          this.#toastMessageService.showErrorMessage(
            err?.error?.title ?? MediaLibraryToastMessages.CREATE_FAILURE
          )
        }

        return throwError(() => err.error)
      })
    )
  }

  triggerFileCloneInWip(image: MediaLibraryResource): Observable<MediaLibraryResource> {
    if (this.#mediaLibraryApi instanceof MediaLibraryNominalApi) {
      return this.#mediaLibraryApi.checkImageVersion(image.id).pipe(
        map((res) => mapResource(res as AppResource<MediaLibraryResource>)),
        map((image: MediaLibraryResource) => this.prepareImageForUi(image)),
        tap(() => {
          this.refresh()
        }),
        catchError((err) => {
          this.#toastMessageService.showErrorMessage()
          return throwError(() => err)
        })
      )
    }

    return of(image)
  }

  updateImage(imageId: string, payloadData: MediaLibraryPayloadData): Observable<any> {
    const formData = this.getFormData(payloadData, ProxyApiAction.UPDATE)

    return this.#mediaLibraryApi.updateImage(imageId, formData).pipe(
      tap(() => {
        this.refresh()
        this.#toastMessageService.showSuccessMessage(MediaLibraryToastMessages.UPDATE_SUCCESS)
      }),
      catchError((err) => {
        this.#toastMessageService.showErrorMessage(
          err?.error?.title ?? MediaLibraryToastMessages.UPDATE_FAILURE
        )

        return throwError(() => err)
      })
    )
  }

  deleteImage(image: MediaLibraryResource): Observable<void> {
    return this.#mediaLibraryApi.deleteImage(image.id).pipe(
      tap(() => {
        this.refresh()
      }),
      catchError((err) => throwError(() => err))
    )
  }

  exportImages(ids?: string[], includeBinaries?: boolean): Observable<any> {
    const globalMediaLibraryApi = this.#mediaLibraryApi instanceof MediaLibraryApi
    let params: any

    if (globalMediaLibraryApi) {
      params = ids ? { ids: ids.join(',') } : {}
    } else {
      params = ids || []
    }

    const exportMethod = includeBinaries
      ? this.#mediaLibraryApi.exportMediaLibraryResourcesWithBinaries(params)
      : this.#mediaLibraryApi.exportMediaLibraryResources(params)

    return exportMethod.pipe(
      map((response) => {
        if (globalMediaLibraryApi && !includeBinaries) {
          return jsonToBlob(response)
        }

        return new Blob([response], { type: 'application/zip' })
      }),
      tap(() =>
        this.#toastMessageService.showSuccessMessage(MediaLibraryToastMessages.EXPORT_SUCCESS)
      ),
      catchError((err) => {
        this.#toastMessageService.showErrorMessage(MediaLibraryToastMessages.EXPORT_FAILURE)
        return throwError(() => new Error(err))
      })
    )
  }

  importImages(files: FileList): Observable<any> {
    const globalMediaLibraryApi = this.#mediaLibraryApi instanceof MediaLibraryApi
    let apiObservable
    if (files.length !== 1) {
      return of()
    }
    const fileToImport = files[0]
    if (globalMediaLibraryApi) {
      if (fileToImport.type === 'application/zip') {
        const formData = new FormData()
        formData.append('file', fileToImport)
        this._importInProgress.next(true)
        apiObservable = this.#mediaLibraryApi.importImages<FormData>(formData, true)
      } else {
        apiObservable = parseFileAsJson(fileToImport).pipe(
          tap(() => this._importInProgress.next(true)),
          switchMap((images: { images: MediaLibraryResource[]; exportVersion: number }) =>
            this.#mediaLibraryApi.importImages<{ images: MediaLibraryResource[] }>(images)
          )
        )
      }
    } else {
      const formData = new FormData()
      formData.append('file', fileToImport)
      this._importInProgress.next(true)
      apiObservable = this.#mediaLibraryApi.importImages<FormData>(formData)
    }

    return apiObservable.pipe(
      tap(() => {
        this.refresh()
        this._importInProgress.next(false)
        this.#toastMessageService.showSuccessMessage(MediaLibraryToastMessages.IMPORT_SUCCESS)
      }),
      catchError((err) => {
        this._importInProgress.next(false)

        this.#toastMessageService.showErrorMessage(
          err?.error?.title || MediaLibraryToastMessages.IMPORT_FAILURE
        )
        return throwError(() => err)
      })
    )
  }

  searchImages(keyword: string): void {
    this.search({ keyword: keyword || '' })
  }

  get importInProgress(): Observable<boolean> {
    return this._importInProgress.asObservable()
  }

  prepareImageForUi(image: MediaLibraryResource): MediaLibraryResource {
    return {
      ...image,
      ...(image.storagePath && {
        storagePath: `${environment.staticAssetsPath}/${image.storagePath}`,
      }),
      ...(image.thumbnailStoragePath && {
        thumbnailStoragePath: `${environment.staticAssetsPath}/${image.thumbnailStoragePath}`,
      }),
    }
  }

  getFormData(payloadData: MediaLibraryPayloadData, action: ProxyApiAction): FormData {
    if (this.#mediaLibraryApi instanceof MediaLibraryApi) {
      const imageInfo = JSON.stringify({
        key: payloadData.key,
        application: 'flowx',
        tags: payloadData.tags,
      })

      payloadData.formData.append('imageInfo', new Blob([imageInfo], { type: 'application/json' }))
    } else {
      const resourceRequest = JSON.stringify({
        resourceName: payloadData.key,
        action,
        payload: {
          key: payloadData.key,
          tags: payloadData.tags,
          contentType: 'application/json',
        },
      })

      payloadData.formData.append(
        'resourceRequest',
        new Blob([resourceRequest], { type: 'application/json' })
      )
    }

    return payloadData.formData
  }
}
